import { Search } from 'lucide-react'
import React, { useMemo } from 'react'
import { toast } from 'sonner'
import z from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'
import { Link, createFileRoute } from '@tanstack/react-router'
import { fallback, zodValidator } from '@tanstack/zod-adapter'
import { useForm } from 'react-hook-form'
import type { DefaultError } from '@tanstack/react-query'
import type { AccessorColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/data-table'
import PageContainer from '@/components/page-container'
import { Button, buttonVariants } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RtlsEye, RtlsPlus, RtlsReload, RtlsTrashAlt } from '@/icons'

import { useDeleteConfirmation } from '@/components/ui/alert-dialog'
import { buildQueryParams, cn } from '@/lib/utils'
import $api from '@/services/api'

const searchParamsSchema = z.object({
  page: fallback(z.number(), 1).default(1),
  limit: fallback(z.number(), 10).default(10),
  keyword: fallback(z.string(), '').default(''),
})

type SearchParamsInput = z.infer<typeof searchParamsSchema>

export const Route = createFileRoute('/admin/management/roles/')({
  component: RoleManagement,
  validateSearch: zodValidator(searchParamsSchema),
})

function RoleManagement() {
  const searchParams = Route.useSearch()
  const page = searchParams.page
  const limit = searchParams.limit

  const navigate = Route.useNavigate()
  const queryClient = Route.useRouteContext().queryClient

  const { showDeleteConfirmation } = useDeleteConfirmation()

  const deleteRoleMutation = $api.useMutation('delete', '/roles/{id}', {
    onSuccess: () => {
      toast.success(`Role deleted successfully`)
      queryClient.invalidateQueries({
        queryKey: $api.queryOptions('get', '/roles').queryKey,
      })
    },
    onError: (error) => {
      toast.error(`Failed to delete role`, {
        description:
          (error as unknown as DefaultError).message || 'Something went wrong',
      })
    },
  })

  const { data, isPending } = $api.useQuery('get', '/roles', {
    params: {
      query: buildQueryParams(searchParams),
    },
  })

  const actions = React.useMemo(
    () => [
      <Link
        to="/admin/management/roles/create"
        key="create"
        className={cn('w-full sm:w-auto', buttonVariants())}
        aria-label="Create new role"
        title="Create new role"
      >
        <RtlsPlus aria-hidden="true" /> Add New Role
      </Link>,
    ],
    [],
  )

  const form = useForm({
    resolver: zodResolver(searchParamsSchema),
    defaultValues: searchParams,
  })

  const onSubmit = (values: SearchParamsInput) => {
    navigate({
      search: values,
    })
  }

  const handleReset = () => {
    form.reset(searchParamsSchema.parse({}))
    form.handleSubmit(onSubmit)()
  }

  const dataSource = useMemo(() => {
    if (!data?.data) return []
    return data.data.map((role, index) => ({
      no: (page - 1) * limit + index + 1,
      id: role.id,
      name: role.name,
    }))
  }, [data, page, limit])

  const columns = React.useMemo<
    Array<AccessorColumnDef<(typeof dataSource)[number], any>>
  >(
    () => [
      {
        accessorKey: 'no',
        header: 'NO',
        size: 80,
        meta: {
          cellClassName: 'text-center',
        },
        enableSorting: false,
      },
      {
        accessorKey: 'id',
        header: 'ID',
        size: 300,
        meta: {
          cellClassName: 'font-semibold',
        },
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'NAME',
      },
      {
        id: 'actions',
        accessorKey: 'id',
        header: 'ACTION',
        size: 100,
        meta: {
          headerClassName: 'text-center',
        },
        enableSorting: false,
        enableHiding: false,
        cell: (info) => {
          const roleId = info.getValue()
          const rowData = info.row.original
          return (
            <ButtonGroup
              role="group"
              aria-label="Role actions"
              className="justify-center"
            >
              <Link
                to="/admin/management/roles/$id"
                params={{ id: roleId }}
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
                aria-label="View role details"
                title="View role details"
              >
                <RtlsEye className="text-primary size-3.5" aria-hidden="true" />
                <span className="sr-only">View</span>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await showDeleteConfirmation({
                    itemName: 'Role',
                    itemDetails: rowData.name,
                    onConfirm: () => {
                      deleteRoleMutation.mutate({
                        params: { path: { id: roleId } },
                      })
                    },
                  })
                }}
                aria-label="Delete role"
                title="Delete role"
                disabled={deleteRoleMutation.isPending}
              >
                <RtlsTrashAlt
                  className="size-3.5 text-neutral-60"
                  aria-hidden="true"
                />
                <span className="sr-only">Delete</span>
              </Button>
            </ButtonGroup>
          )
        },
      },
    ],
    [],
  )

  return (
    <PageContainer title="Role Management" actions={actions}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          aria-label="Search roles"
          role="search"
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            <FormField
              control={form.control}
              name="keyword"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        placeholder="Search"
                        className="pr-10"
                        aria-label="Search roles by name"
                        aria-describedby="search-help"
                      />
                      <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 size-4 text-muted-foreground" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div
              className="flex flex-col md:flex-row gap-3 "
              role="group"
              aria-label="Search actions"
            >
              <Button
                type="submit"
                loading={isPending}
                aria-label="Apply search filters"
                aria-busy={isPending}
              >
                Apply
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={isPending}
                aria-label="Reset search filters"
                aria-disabled={isPending}
              >
                <RtlsReload aria-hidden="true" />
                Reset
              </Button>
            </div>
          </div>
          <div id="search-help" className="sr-only">
            Search for roles by name. Results will update when submitted.
          </div>
        </form>
      </Form>
      <DataTable
        columns={columns}
        loading={isPending}
        data={dataSource}
        aria-label="Roles table"
        columnPinning={{
          right: ['actions'],
        }}
        pagination={{
          page: Number(page),
          pageSize: Number(limit),
          onChange: (updater) => {
            navigate({
              search: (s) => ({
                ...s,
                page: updater.page,
                limit: updater.pageSize,
              }),
            })
          },
        }}
      />
    </PageContainer>
  )
}
